import { Injectable, InjectionToken, ComponentRef } from "@angular/core";
import { ValidationErrors, AbstractControl } from "@angular/forms";
import { FieldType } from "./../templates/field.type";
import { reverseDeepMerge, defineHiddenProp } from "./../utils";
import {
  KreFormFieldConfig,
  KreFormFieldConfigCache,
} from "../components/kre-form.field.config";
import { Observable } from "rxjs";

export const KreFORM_CONFIG = new InjectionToken<KreFormConfig>(
  "KreFORM_CONFIG"
);

export interface KreFormExtension {
  prePopulate?(field: KreFormFieldConfig): void;
  onPopulate?(field: KreFormFieldConfig): void;
  postPopulate?(field: KreFormFieldConfig): void;
}

@Injectable({ providedIn: "root" })
export class KreFormConfig {
  types: { [name: string]: TypeOption } = {};
  validators: { [name: string]: ValidatorOption } = {};
  wrappers: { [name: string]: WrapperOption } = {};
  messages: { [name: string]: ValidationMessageOption["message"] } = {};
  templateManipulators: {
    preWrapper: ManipulatorWrapper[];
    postWrapper: ManipulatorWrapper[];
  } = {
    preWrapper: [],
    postWrapper: [],
  };
  extras: ConfigOption["extras"] = {
    checkExpressionOn: "changeDetectionCheck",
    lazyRender: false,
    showError: function (field: FieldType) {
      return (
        field.formControl &&
        field.formControl.invalid &&
        ((field.formControl.touched && field.formControl.dirty) ||
          (field.options.parentForm && field.options.parentForm.submitted) ||
          !!(field.field.validation && field.field.validation.show))
      );
    },
  };
  extensions: { [name: string]: KreFormExtension } = {};

  appConfig: {
    url?: string;
    token?: string;
  };

  addConfig(config: ConfigOption) {
    if (config.types) {
      config.types.forEach((type) => this.setType(type));
    }
    if (config.validators) {
      config.validators.forEach((validator) => this.setValidator(validator));
    }
    if (config.wrappers) {
      config.wrappers.forEach((wrapper) => this.setWrapper(wrapper));
    }
    if (config.manipulators) {
      config.manipulators.forEach((manipulator) =>
        this.setManipulator(manipulator)
      );
    }
    if (config.validationMessages) {
      config.validationMessages.forEach((validation) =>
        this.addValidatorMessage(validation.name, validation.message)
      );
    }
    if (config.extensions) {
      config.extensions.forEach((c) => (this.extensions[c.name] = c.extension));
    }
    if (config.extras) {
      this.extras = { ...this.extras, ...config.extras };
    }
  }

  setType(options: TypeOption | TypeOption[]) {
    if (Array.isArray(options)) {
      options.forEach((option) => this.setType(option));
    } else {
      if (!this.types[options.name]) {
        this.types[options.name] = <TypeOption>{ name: options.name };
      }

      ["component", "extends", "defaultOptions", "wrappers"].forEach((prop) => {
        if (options.hasOwnProperty(prop)) {
          this.types[options.name][prop] = options[prop];
        }
      });
    }
  }

  getType(name: string): TypeOption {
    if (!this.types[name]) {
    }

    this.mergeExtendedType(name);

    return this.types[name];
  }

  getMergedField(field: KreFormFieldConfig = {}): any {
    const type = this.getType(field.type);
    if (type.defaultOptions) {
      reverseDeepMerge(field, type.defaultOptions);
    }

    const extendDefaults =
      type.extends && this.getType(type.extends).defaultOptions;
    if (extendDefaults) {
      reverseDeepMerge(field, extendDefaults);
    }

    if (field && field.optionsTypes) {
      field.optionsTypes.forEach((option) => {
        const defaultOptions = this.getType(option).defaultOptions;
        if (defaultOptions) {
          reverseDeepMerge(field, defaultOptions);
        }
      });
    }

    const componentRef = this.resolveFieldTypeRef(field);
    if (
      componentRef &&
      componentRef.instance &&
      componentRef.instance.defaultOptions
    ) {
      reverseDeepMerge(field, componentRef.instance.defaultOptions);
    }

    if (!field.wrappers && type.wrappers) {
      field.wrappers = [...type.wrappers];
    }
  }

  resolveFieldTypeRef(
    field: KreFormFieldConfigCache = {}
  ): ComponentRef<FieldType> {
    if (!field.type) {
      return null;
    }

    const type = this.getType(field.type);
    if (!type.component || type["_componentRef"]) {
      return type["_componentRef"];
    }

    const { _resolver, _injector } = field.parent.options;
    const componentRef = _resolver
      .resolveComponentFactory<FieldType>(type.component)
      .create(_injector);

    defineHiddenProp(type, "_componentRef", componentRef);
    componentRef.destroy();

    return type["_componentRef"];
  }

  setWrapper(options: WrapperOption) {
    this.wrappers[options.name] = options;
    if (options.types) {
      options.types.forEach((type) => {
        this.setTypeWrapper(type, options.name);
      });
    }
  }

  getWrapper(name: string): WrapperOption {
    if (!this.wrappers[name]) {
    }

    return this.wrappers[name];
  }

  setTypeWrapper(type: string, name: string) {
    if (!this.types[type]) {
      this.types[type] = <TypeOption>{};
    }
    if (!this.types[type].wrappers) {
      this.types[type].wrappers = [];
    }
    if (this.types[type].wrappers.indexOf(name) === -1) {
      this.types[type].wrappers.push(name);
    }
  }

  setValidator(options: ValidatorOption) {
    this.validators[options.name] = options;
  }

  getValidator(name: string): ValidatorOption {
    if (!this.validators[name]) {
    }

    return this.validators[name];
  }

  addValidatorMessage(
    name: string,
    message: ValidationMessageOption["message"]
  ) {
    this.messages[name] = message;
  }

  getValidatorMessage(name: string) {
    return this.messages[name];
  }

  setManipulator(manipulator: ManipulatorOption) {
    new manipulator.class()[manipulator.method](this);
  }

  setAPPConfig(config: APPConfig) {
    this.appConfig = config;
  }

  private mergeExtendedType(name: string) {
    if (!this.types[name]["extends"]) {
      return;
    }

    const extendedType = this.getType(this.types[name].extends);
    if (!this.types[name].component) {
      this.types[name].component = extendedType.component;
    }

    if (!this.types[name].wrappers) {
      this.types[name].wrappers = extendedType.wrappers;
    }
  }
}
export interface TypeOption {
  name: string;
  component?: any;
  wrappers?: string[];
  extends?: string;
  defaultOptions?: KreFormFieldConfig;
}

export interface WrapperOption {
  name: string;
  component: any;
  types?: string[];
}

export interface FieldValidatorFn {
  (
    c: AbstractControl,
    field: KreFormFieldConfig,
    options?: { [id: string]: any }
  ): ValidationErrors | null;
}

export interface ValidatorOption {
  name: string;
  validation: FieldValidatorFn;
  options?: { [id: string]: any };
}

export interface ExtensionOption {
  name: string;
  extension: KreFormExtension;
}

export interface ValidationMessageOption {
  name: string;
  message:
    | string
    | ((error: any, field: KreFormFieldConfig) => string | Observable<string>);
}

export interface ManipulatorOption {
  class?: { new (): any };
  method?: string;
}

export interface ManipulatorWrapper {
  (f: KreFormFieldConfig): string;
}

export interface TemplateManipulators {
  preWrapper?: ManipulatorWrapper[];
  postWrapper?: ManipulatorWrapper[];
}

export interface ConfigOption {
  types?: TypeOption[];
  wrappers?: WrapperOption[];
  validators?: ValidatorOption[];
  extensions?: ExtensionOption[];
  validationMessages?: ValidationMessageOption[];

  manipulators?: ManipulatorOption[];
  extras?: {
    fieldTransform?: any;
    immutable?: boolean;
    showError?: (field: FieldType) => boolean;

    checkExpressionOn?: "modelChange" | "changeDetectionCheck";

    lazyRender?: boolean;

    resetFieldOnHide?: boolean;
  };
}

export interface APPConfig {
  url?: string;
  token?: string;
}
